import { publicApi } from "@api";
import GetCodeButton from "@components/inputs/GetCodeButton";
import { GetCodeButtonRef } from "@components/inputs/GetCodeButton/GetCodeButton";
import PasswordTextField from "@components/inputs/PasswordTextField";
import { usePostLoginCheck } from "@components/utils/Auth/hooks";
import { ForgotSecretCallbackProps } from "@components/utils/Auth/types/auth-props";
import { envConfig } from "@configs/env";
import { OTPMethod, OTPSource, useStatsigTracking } from "@configs/statsig";
import { ValidatorSchema } from "@constants/validators";
import { zodResolver } from "@hookform/resolvers/zod";
import useCaptureFormValidationErrors from "@hooks/useCaptureFormValidationErrors";
import { useLoginSubmit } from "@hooks/useLoginSubmit";
import { useMemberSettings } from "@hooks/useMemberSettings";
import { resolveAuthResponseError } from "@utils/error-resolvers";
import { getOTPCodeSentMessage } from "@utils/message";
import {
  APIErrorCodes,
  Button,
  PropsWithClassName,
  TextField,
  cn,
  delay,
  standardizePhoneNumber,
  useFieldHelperText,
  useHandleApiResponse,
} from "kz-ui-sdk";
import moment from "moment";
import { useCallback, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { MdMail, MdPhoneAndroid } from "react-icons/md";
import { z } from "zod";

interface ForgotSecretUPProps extends ForgotSecretCallbackProps, PropsWithClassName {
  defaultPhone?: string;
  phoneReadonly?: boolean;
}

const inputClasses = {
  "input&": "w-full !px-0 !pl-11 text-start",
  "iconContainer&": "!left-3 !top-[10px]",
};

const ForgotSecretUP = ({
  defaultPhone,
  phoneReadonly,
  onLoginSuccess,
  onForgotSecretSuccess,
  onPostLoginCheckFailed,
  onPostLoginCheckPassed,
  className,
}: ForgotSecretUPProps) => {
  const { t, i18n } = useTranslation();
  const { phonePrefix, phoneMinLength, phoneMaxLength, phoneStartWith, showPhoneInputPrefix, phoneSimpleError } =
    useMemberSettings();
  const [sendOTP, { isLoading: isSendingOTP }] = publicApi.useCreateForgotPasswordMutation();
  const [verifyOTP, { isLoading: isVerifyingOTP }] = publicApi.useVerifyOTPMutation();
  const [submitForgotPwd, { isLoading: isSubmittingForgotPwd }] = publicApi.useSubmitResetPasswordMutation();
  const getCodeButtonRef = useRef<GetCodeButtonRef>(null);
  const { handleApiResponse } = useHandleApiResponse({ toast });
  const postLoginCheck = usePostLoginCheck({
    onLoginSuccess,
    onPostLoginCheckPassed,
    onPostLoginCheckFailed,
  });
  const { trackOTPSent, trackOTPRequestFailed, trackOTPVerified, trackOTPFailedAttempt, trackOTPRequest } =
    useStatsigTracking();

  const forgotPasswordFormSchema = useMemo(() => {
    return z.object({
      phone: ValidatorSchema.phone({
        minLength: phoneMinLength,
        maxLength: phoneMaxLength,
        startWith: phoneStartWith,
        simpleError: phoneSimpleError,
        msgType: "digits",
      }),
      sms: ValidatorSchema.otp,
      password: ValidatorSchema.registerPassword,
    });
  }, [phoneMinLength, phoneMaxLength, phoneStartWith, phoneSimpleError]);

  const {
    handleSubmit: handleFormSubmit,
    register: formRegister,
    watch,
    formState: { errors: formErrors, submitCount },
    trigger,
    getValues,
    setError,
  } = useForm<z.infer<typeof forgotPasswordFormSchema>>({
    mode: "onChange",
    resolver: zodResolver(forgotPasswordFormSchema),
    defaultValues: {
      phone: defaultPhone,
    },
  });

  const { handleLogin, isLoading: isLoginLoading } = useLoginSubmit({
    onLoginSuccess: postLoginCheck,
  });

  // Capture form validation errors
  useCaptureFormValidationErrors(formErrors, {
    watch,
    feature: "forgot-password",
  });

  const fieldHelperTextManager = useFieldHelperText({
    submitCount: submitCount,
    trigger: trigger,
    errors: formErrors,
  });

  const onSubmitForm = async (data: z.infer<typeof forgotPasswordFormSchema>) => {
    const { phone, sms, password } = data;
    try {
      await handleVerifyOTP(sms, phone);

      const resSubmitForgotPwd = await submitForgotPwd({
        accountId: envConfig.accountId,
        phone: standardizePhoneNumber(phone, phonePrefix),
        code: sms,
        password,
      });

      handleApiResponse(resSubmitForgotPwd, {
        successMessage: t("password updated successfully"),
        toastSuccess: true,
        toastError: false,
        onSuccess: (response) => {
          const clientId = response?.accessKey;
          if (clientId) {
            handleLogin(clientId, password);
          }
          onForgotSecretSuccess?.();
        },
        onError: (error) => {
          if (error?.data?.error?.message === APIErrorCodes.Member.PhoneNotRegistered) {
            setError("phone", { message: t("number not registered. please try another") });
            return;
          }
          toast.error(resolveAuthResponseError(error, t));
        },
      });
    } catch (error) {
      setError("sms", { message: error as string });
    }
  };

  const handleGetCode = async () => {
    return new Promise((resolve, reject) => {
      trigger(["phone"]).then((isFormValid) => {
        if (!isFormValid) {
          reject(new Error("Form is not valid"));
          return;
        }

        const { phone } = getValues();
        trackOTPRequest(OTPMethod.SMS, { source: OTPSource.FORGOT_PASSWORD });
        sendOTP({
          phone: standardizePhoneNumber(phone, phonePrefix),
          accountId: envConfig.accountId,
        }).then((response) => {
          handleApiResponse(response, {
            toastSuccess: false,
            toastError: false,
            onSuccess: (otpRes) => {
              toast.success(getOTPCodeSentMessage(otpRes.method, i18n.language, t));
              const nextRequestAt = otpRes.nextRequestAt;
              const remainingSeconds = moment(nextRequestAt).diff(moment(), "seconds");
              getCodeButtonRef.current?.startTimer(remainingSeconds);
              // Track OTP sent with forgot source
              trackOTPSent(otpRes.method as OTPMethod, { source: OTPSource.FORGOT_PASSWORD });
              resolve(otpRes);
            },
            onError: (error) => {
              if (error?.data?.error?.message === APIErrorCodes.Member.PhoneNotRegistered) {
                setError("phone", { message: t("number not registered. please try another") });
                return;
              }
              const message = resolveAuthResponseError(error, t);
              if (message) {
                toast.error(message);
              }
              trackOTPRequestFailed(error, { source: OTPSource.FORGOT_PASSWORD });
              reject(error);
            },
          });
        });
      });
    });
  };

  const handleVerifyOTP = useCallback(
    async (code: string, phone: string) => {
      return new Promise((resolve, reject) => {
        verifyOTP({
          phone: standardizePhoneNumber(phone, phonePrefix),
          code: code,
          accountId: envConfig.accountId,
        }).then((response) => {
          handleApiResponse(response, {
            toastError: false,
            toastSuccess: false,
            onSuccess: (res) => {
              trackOTPVerified(OTPMethod.SMS, { source: OTPSource.FORGOT_PASSWORD });
              resolve(res);
            },
            onError: (error) => {
              if (error?.data?.error?.message) {
                setError("sms", { message: t(error?.data?.error.message) });
              }
              trackOTPFailedAttempt(error, { source: OTPSource.FORGOT_PASSWORD });
              reject(error?.data?.error?.message);
            },
          });
        });
      });
    },
    [handleApiResponse, t, verifyOTP, setError, phonePrefix, trackOTPVerified, trackOTPFailedAttempt],
  );

  const isConfirming = isVerifyingOTP || isSubmittingForgotPwd;

  return (
    <form
      id="form-forgot-secret-username-password"
      name="form-forgot-secret-username-password"
      onSubmit={handleFormSubmit((e) => delay().then(() => onSubmitForm(e)))}
      className={cn("flex flex-col gap-3", className)}
      autoComplete="off"
    >
      {/* PHONE NUMBER */}
      <TextField
        {...formRegister("phone")}
        error={!!formErrors.phone}
        helperText={t(formErrors.phone?.message ?? "", {
          minLength: phoneMinLength,
          maxLength: phoneMaxLength,
          startWith: Array.isArray(phoneStartWith) ? phoneStartWith.join(", ") : "0",
        })}
        placeholder={t("phone number")}
        type={"number"}
        inputMode={"numeric"}
        icon={<MdPhoneAndroid className="text-2xl text-content-base" />}
        classes={{
          ...inputClasses,
          "input&": inputClasses["input&"] + (showPhoneInputPrefix ? " !pl-20" : ""),
          "prefixContainer&": showPhoneInputPrefix ? "absolute left-11 top-[10px] text-content-base" : "",
        }}
        size="sm"
        autoComplete="off"
        readOnly={phoneReadonly}
        prefix={showPhoneInputPrefix ? phonePrefix : undefined}
        fieldHelperTextManager={fieldHelperTextManager}
      />

      {/* OTP */}
      <div className="relative">
        <TextField
          {...formRegister("sms")}
          error={!!formErrors.sms}
          helperText={t(formErrors.sms?.message as string)}
          placeholder={t("enter code")}
          type={"text"}
          icon={<MdMail className="text-2xl text-content-base" />}
          classes={inputClasses}
          size="sm"
          autoComplete="one-time-code"
          inputMode="numeric"
          fieldHelperTextManager={fieldHelperTextManager}
        />
        <GetCodeButton
          onGetCode={handleGetCode}
          className="absolute right-3 top-[9px]"
          classes={{
            "root&": "w-fit px-2",
          }}
          loading={isSendingOTP}
          ref={getCodeButtonRef}
        />
      </div>

      {/* PASSWORD */}
      <PasswordTextField
        {...formRegister("password")}
        error={!!formErrors.password}
        helperText={t(formErrors.password?.message as string)}
        placeholder={t("password")}
        classes={inputClasses}
        buttonClass="!top-[10px]"
        size="sm"
        autoComplete="new-password"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        fieldHelperTextManager={fieldHelperTextManager}
      />

      <Button
        variant="primary"
        size="lg"
        type="submit"
        className="mt-2"
        disabled={isConfirming || isLoginLoading}
        loading={isConfirming || isLoginLoading}
      >
        {t("confirm")}
      </Button>
    </form>
  );
};

export default ForgotSecretUP;
